/**
 * Feature flag to control the visibility of a feature.
 * This flag is enabled only in staging and development environments.
 */
export const exampleFlag =
  import.meta.env.MODE === 'staging' || import.meta.env.MODE === 'development';

/**
 * Controls visibility of internal/dev-only views (e.g. the design system gallery).
 * Enabled in staging and development environments.
 */
export const isInternalViewsEnabled =
  import.meta.env.MODE === 'staging' || import.meta.env.MODE === 'development';

/**
 * Feature flag to control PostHog initialization.
 * Analytics are restricted to the production environment to preserve
 * the 1M event free-tier quota and ensure data integrity.
 */
export const isPostHogEnabled: boolean =
  import.meta.env.MODE !== 'staging' &&
  import.meta.env.MODE !== 'development' &&
  import.meta.env.VITE_APP_POSTHOG_KEY !== undefined &&
  import.meta.env.VITE_APP_POSTHOG_KEY !== '';

/**
 * Controls the consent banner and its local consent record. The record captures
 * legal acceptance (EULA/T&C) as well as analytics opt-in, so it's decoupled
 * from `isPostHogEnabled` (prod-only) to let consent-gated features be tested on
 * staging. Enabled everywhere except local development.
 */
export const isConsentBannerEnabled: boolean = import.meta.env.MODE !== 'development';

/**
 * True only on staging. Used to relax consent-gated integrations (e.g. Userback)
 * that can't depend on a per-user consent state on staging, where users aren't
 * added to PostHog. Keyed on MODE rather than `isPostHogEnabled` on purpose: a
 * missing PostHog key must never silently relax a consent gate in production.
 */
export const isStaging: boolean = import.meta.env.MODE === 'staging';

/**
 * True only in production (MODE is neither staging nor development) — mirrors the
 * environment split `isPostHogEnabled` uses. Gates prod-only UI whose backing BE
 * config is only real in production; the first case is the Activity Log
 * pre-release banner, whose `ActivityLog:LaunchDate` is a far-future sentinel
 * outside production (so `entityCreatedBeforeFeatureRelease` is true for every
 * entity there and the banner would otherwise show on all of them).
 */
export const isProduction: boolean =
  import.meta.env.MODE !== 'staging' && import.meta.env.MODE !== 'development';
